import {
  useQuery,
  type QueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { useDebouncedValue } from '../../../use-debounced-value/use-debounced-value';
import {
  userRoleShape,
  type UserRole,
  sourceSystemShape,
} from '../../../../utils/api/types/access-api-types';
import type { ApiError } from '../../../../utils/api/ApiError/ApiError';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';

const pageableShape = z.object({
  pageable: z.object({
    pageNumber: z.number(),
    pageSize: z.number(),
    sort: z.object({
      sorted: z.boolean(),
      unsorted: z.boolean(),
      empty: z.boolean(),
    }),
    offset: z.number(),
    unpaged: z.boolean(),
    paged: z.boolean(),
  }),
  last: z.boolean(),
  totalPages: z.number(),
  totalElements: z.number(),
  number: z.number(),
  first: z.boolean(),
  size: z.number(),
  numberOfElements: z.number(),
  empty: z.boolean(),
});

export const compositeUserShape = z.object({
  email: z.string(),
  type: z.string(),
  username: z.string().optional(),
  user_id: z.string(),
  given_name: z.string(),
  family_name: z.string(),
  source_systems: z.array(sourceSystemShape),
  org_info: z.array(
    z.object({
      name: z.string(),
      organization_id: z.string(),
      user_role: userRoleShape,
    })
  ),
});

const usersByOrgIdWithPaginationShape = z.object({
  content: z.array(compositeUserShape),
  ...pageableShape.shape,
});

export type PageableUserSortableFields = 'email' | 'family_name';
export type PageableSortDirections = 'asc' | 'desc';
export type PageableUserSort = [
  PageableUserSortableFields,
  PageableSortDirections
];

type PaginationData = {
  page: number | string;
  size: number | string;
  sort: PageableUserSort;
};

type GetUsersByOrgIdWithPaginationParams = {
  orgId: string;
  roles: UserRole[];
  searchValue?: string;
  includeChildren?: boolean;
};

export type CompositeUsersWithPagination = z.infer<
  typeof usersByOrgIdWithPaginationShape
>;
export type CompositeUser = z.infer<typeof compositeUserShape>;

export const defaultUserPaginationData: PaginationData = {
  page: 0,
  size: 10,
  sort: ['family_name', 'asc'],
};

export async function getUsersByOrgIdWithPaginationApi(
  {
    orgId,
    roles,
    searchValue,
    includeChildren = true,
  }: GetUsersByOrgIdWithPaginationParams,
  paginationData?: PaginationData
): Promise<CompositeUsersWithPagination> {
  const { sort, ...basePaginationData } = {
    ...defaultUserPaginationData,
    ...paginationData,
  };

  const users = await fetcher<CompositeUsersWithPagination>(
    `/composite/users/orgs/${orgId}`,
    {
      service: '/roster',
      version: '/v3',
      queryParameters: [
        ...roles.map((role) => ['userRoles', role]),
        ['searchValue', searchValue],
        ['sort', sort.join(',')],
        ['includeChildren', includeChildren],
        ...Object.entries(basePaginationData),
      ] as string[][],
    }
  );

  return usersByOrgIdWithPaginationShape.parse(users);
}

export const getUsersByOrgIdWithPaginationApiQueryKey = (
  args?: Partial<GetUsersByOrgIdWithPaginationParams> & Partial<PaginationData>
) => {
  const { orgId, roles, page, size, sort, searchValue, includeChildren } =
    args ?? {};

  return [
    '/v3',
    '/roster/composite/users',
    orgId,
    roles,
    page,
    size,
    sort,
    searchValue,
    includeChildren,
  ].filter(Boolean);
};

export function useGetUsersByOrgIdWithPaginationApi(
  args: Partial<GetUsersByOrgIdWithPaginationParams>,
  paginationData: PaginationData = defaultUserPaginationData,
  customQueryOptions?: QueryOptions<CompositeUsersWithPagination, ApiError>
): UseQueryResult<CompositeUsersWithPagination, ApiError> {
  const { orgId, roles, searchValue, includeChildren } = args;
  const [debouncedSearchValue] = useDebouncedValue(searchValue, 200);

  return useQuery({
    queryKey: getUsersByOrgIdWithPaginationApiQueryKey({
      orgId,
      roles,
      searchValue: debouncedSearchValue,
      includeChildren,
      ...paginationData,
    }),
    queryFn: () =>
      getUsersByOrgIdWithPaginationApi(
        {
          orgId: orgId!,
          roles: roles!,
          searchValue,
          includeChildren,
        },
        paginationData
      ),
    enabled: !!orgId && !!roles?.length,
    staleTime: Infinity,
    ...customQueryOptions,
  });
}

export { pageableShape };
